import { useCallback, useEffect, useState } from 'react';

import { cn } from '@crossnokaye/ux-common';

import { useTable } from '@/components/data-table/core/context';

import type { CellHighlightProps } from './cell-highlight.types';
import {
  areOverlaysEqual,
  buildOverlayPositions,
  type OverlayPosition,
} from './cell-highlight.utils';

export const CellHighlight = ({ targets, className }: CellHighlightProps) => {
  const { table } = useTable();
  const [overlays, setOverlays] = useState<OverlayPosition[]>([]);

  const tableElement = table.options.meta?.tableRef?.current;

  const updateOverlays = useCallback(() => {
    if (!tableElement || targets.length === 0) {
      setOverlays([]);
      return;
    }
    // Virtualized tables only render in-DOM rows, so overlays are limited to visible rows.
    const nextOverlays = buildOverlayPositions(tableElement, targets, className);

    setOverlays((currentOverlays) =>
      areOverlaysEqual(currentOverlays, nextOverlays) ? currentOverlays : nextOverlays
    );
  }, [className, tableElement, targets]);

  useEffect(() => {
    if (!tableElement) return;

    const observer = new ResizeObserver(() => {
      updateOverlays();
    });

    observer.observe(tableElement);

    return () => {
      observer.disconnect();
    };
  }, [tableElement, updateOverlays]);

  const columnSizing = table.getState().columnSizing;
  const sorting = table.getState().sorting;
  const rowSelection = table.getState().rowSelection;

  useEffect(() => {
    updateOverlays();
  }, [columnSizing, rowSelection, sorting, targets, updateOverlays]);

  if (overlays.length === 0) return null;

  return (
    <>
      {overlays.map((overlay) => (
        <div
          key={overlay.key}
          data-testid={`cell-highlight-${overlay.key}`}
          className={cn('absolute pointer-events-none z-[11]', overlay.className)}
          style={{
            left: `${overlay.left}px`,
            top: `${overlay.top}px`,
            width: `${overlay.width}px`,
            height: `${overlay.height}px`,
          }}
        />
      ))}
    </>
  );
};

CellHighlight.displayName = 'DataTable.CellHighlight';
